/**
 * SplatEditModel — the authoritative CPU copy of a splat being edited.
 *
 * Holds the decoded gaussian columns (PLY property names), one state byte per
 * splat, per-splat transform and colour palette indices, and caches derived
 * from them (world-space centers and radii). Every selection tool and the save
 * path read from here, so they behave identically on WebGL2 and WebGPU and can
 * be unit tested without a graphics device. The renderer mirrors state and
 * palettes to GPU textures.
 */
import { SplatState } from './SplatState';
import { ColorPalette, TransformPalette, TRANSFORM_FLOATS } from './Palettes';

/** Minimal read interface satisfied by PlayCanvas GSplatData. */
export interface SplatColumnSource {
    numSplats: number;
    getProp(name: string): ArrayLike<number> | null | undefined;
}

export const CORE_PROPS = [
    'x', 'y', 'z',
    'scale_0', 'scale_1', 'scale_2',
    'rot_0', 'rot_1', 'rot_2', 'rot_3',
    'f_dc_0', 'f_dc_1', 'f_dc_2',
    'opacity',
] as const;

export type CoreProp = typeof CORE_PROPS[number];

export const SH_C0 = 0.28209479177387814;

/** Number of f_rest coefficients per colour channel for a band count. */
export const SH_COEFFS_PER_CHANNEL = [0, 3, 8, 15] as const;

export function shBandsFromRestCount(count: number): number {
    if (count >= 45) return 3;
    if (count >= 24) return 2;
    if (count >= 9) return 1;
    return 0;
}

export interface SplatEditCounts {
    total: number;
    selected: number;
    hidden: number;
    deleted: number;
}

export class SplatEditModel {
    numSplats: number;
    /** Decoded columns keyed by PLY property name (core props + f_rest_*). */
    columns: Map<string, Float32Array>;
    shBands: number;

    state: Uint8Array;
    transformIndex: Uint32Array;
    colorIndex: Uint32Array;
    readonly transformPalette = new TransformPalette();
    readonly colorPalette = new ColorPalette();

    /** Entity world matrix (column-major) the model is rendered with. */
    readonly worldMatrix = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);

    stateVersion = 0;
    transformVersion = 0;
    colorVersion = 0;
    /** Bumped when rows are appended or removed (duplicate / undo). */
    rowsVersion = 0;
    /** Bumped when preview marks change (they don't count as a state change). */
    marksVersion = 0;
    /** Bumped when any splat's hidden / deleted bit changes (not on selection changes). */
    liveVersion = 0;
    private _markedCount = 0;
    /** Rows carrying SplatState.previewHidden. */
    private _previewHidden: number[] = [];

    private _counts: SplatEditCounts = { total: 0, selected: 0, hidden: 0, deleted: 0 };
    private _countsValid = false;

    private _worldCenters: Float32Array | null = null;
    private _worldCentersKey = '';
    private _worldRadii: Float32Array | null = null;
    private _worldRadiiKey = '';

    constructor(source: SplatColumnSource, options: { initialState?: Uint8Array } = {}) {
        const n = source.numSplats;
        this.numSplats = n;
        this.columns = new Map();

        for (const name of CORE_PROPS) {
            const col = source.getProp(name);
            if (col) {
                this.columns.set(name, toFloat32(col, n));
            } else if (name.startsWith('rot_')) {
                const filled = new Float32Array(n);
                if (name === 'rot_0') filled.fill(1);
                this.columns.set(name, filled);
            } else if (name === 'opacity') {
                // fully opaque in logit space
                this.columns.set(name, new Float32Array(n).fill(10));
            } else {
                this.columns.set(name, new Float32Array(n));
            }
        }

        let rest = 0;
        for (let i = 0; i < 45; i++) {
            const col = source.getProp(`f_rest_${i}`);
            if (!col) break;
            this.columns.set(`f_rest_${i}`, toFloat32(col, n));
            rest++;
        }
        this.shBands = shBandsFromRestCount(rest);
        // keep only whole bands
        const keep = SH_COEFFS_PER_CHANNEL[this.shBands] * 3;
        for (let i = keep; i < rest; i++) this.columns.delete(`f_rest_${i}`);

        this.state = new Uint8Array(n);
        if (options.initialState) this.state.set(options.initialState.subarray(0, n));
        this.transformIndex = new Uint32Array(n);
        this.colorIndex = new Uint32Array(n);
    }

    col(name: CoreProp | string): Float32Array {
        const c = this.columns.get(name);
        if (!c) throw new Error(`SplatEditModel: missing column ${name}`);
        return c;
    }

    /** Names of the f_rest columns present, in order. */
    shColumnNames(): string[] {
        const count = SH_COEFFS_PER_CHANNEL[this.shBands] * 3;
        const names: string[] = [];
        for (let i = 0; i < count; i++) names.push(`f_rest_${i}`);
        return names;
    }

    // ─── State bookkeeping ─────────────────────────────────

    /** `live`: hidden / deleted bits may have changed (false for selection-only changes). */
    markStateChanged(live = true): void {
        this.stateVersion++;
        if (live) this.liveVersion++;
        this._countsValid = false;
    }

    /** Number of splats currently marked (SplatState.marked). */
    get markedCount(): number {
        return this._markedCount;
    }

    /**
     * Set the display-only preview mark on the live splats in `mask` and clear
     * it everywhere else (null clears all). Not undoable and not a state change:
     * selections, counts and history ignore it. Returns the number marked.
     */
    setMarked(mask: ArrayLike<number> | null): number {
        const state = this.state;
        const marked = SplatState.marked;
        if (!mask && this._markedCount === 0) return 0;
        let count = 0;
        for (let i = 0; i < this.numSplats; i++) {
            const s = state[i];
            const on = !!mask && !!mask[i] && !(s & (SplatState.hidden | SplatState.deleted));
            state[i] = on ? s | marked : s & ~marked;
            if (on) count++;
        }
        this._markedCount = count;
        this.marksVersion++;
        return count;
    }

    /**
     * Stop drawing these splats (rows[from..]) until clearPreviewHidden. Display
     * only, like setMarked: not undoable and not a state change. A replace
     * stroke uses it so what it paints over disappears as it paints.
     */
    hidePreview(rows: ArrayLike<number>, from = 0): void {
        const state = this.state;
        const bit = SplatState.previewHidden;
        const before = this._previewHidden.length;
        for (let k = from; k < rows.length; k++) {
            const i = rows[k];
            if (i >= 0 && i < this.numSplats && !(state[i] & bit)) {
                state[i] |= bit;
                this._previewHidden.push(i);
            }
        }
        if (this._previewHidden.length !== before) this.marksVersion++;
    }

    /** Draw everything hidePreview hid again. */
    clearPreviewHidden(): void {
        if (!this._previewHidden.length) return;
        const state = this.state;
        const keep = ~SplatState.previewHidden;
        for (const i of this._previewHidden) if (i < this.numSplats) state[i] &= keep;
        this._previewHidden = [];
        this.marksVersion++;
    }

    get previewHiddenCount(): number {
        return this._previewHidden.length;
    }

    markTransformChanged(): void {
        this.transformVersion++;
        this._worldCenters = null;
        this._worldRadii = null;
    }

    markColorChanged(): void {
        this.colorVersion++;
    }

    get counts(): SplatEditCounts {
        if (!this._countsValid) {
            let selected = 0, hidden = 0, deleted = 0;
            const s = this.state;
            for (let i = 0; i < this.numSplats; i++) {
                const v = s[i];
                if (v & SplatState.deleted) { deleted++; continue; }
                if (v & SplatState.hidden) hidden++;
                if (v & SplatState.selected) selected++;
            }
            this._counts = { total: this.numSplats, selected, hidden, deleted };
            this._countsValid = true;
        }
        return { ...this._counts };
    }

    /** True when splat `i` may be picked up by a selection tool. */
    isSelectable(i: number): boolean {
        return (this.state[i] & (SplatState.hidden | SplatState.deleted)) === 0;
    }

    setWorldMatrix(mat4: ArrayLike<number>): void {
        let changed = false;
        for (let i = 0; i < 16; i++) {
            if (this.worldMatrix[i] !== mat4[i]) {
                this.worldMatrix[i] = mat4[i];
                changed = true;
            }
        }
        if (changed) {
            this._worldCenters = null;
            this._worldRadii = null;
        }
    }

    // ─── Derived world-space caches ───────────────────────

    /**
     * World-space center of every splat (3 floats each), including per-splat
     * palette transforms. Cached until a transform or the world matrix changes.
     */
    getWorldCenters(): Float32Array {
        const key = `${this.transformVersion}:${this.rowsVersion}`;
        if (this._worldCenters && this._worldCentersKey === key) return this._worldCenters;

        const n = this.numSplats;
        const out = this._worldCenters && this._worldCenters.length === n * 3
            ? this._worldCenters : new Float32Array(n * 3);
        const x = this.col('x'), y = this.col('y'), z = this.col('z');
        const ti = this.transformIndex;
        const combined = this.combinedTransforms();
        const w = this.worldMatrix;
        const w0 = w[0], w1 = w[1], w2 = w[2], w4 = w[4], w5 = w[5], w6 = w[6];
        const w8 = w[8], w9 = w[9], w10 = w[10], w12 = w[12], w13 = w[13], w14 = w[14];

        for (let i = 0; i < n; i++) {
            const px = x[i], py = y[i], pz = z[i];
            const t = ti[i];
            if (t === 0) {
                out[i * 3] = w0 * px + w4 * py + w8 * pz + w12;
                out[i * 3 + 1] = w1 * px + w5 * py + w9 * pz + w13;
                out[i * 3 + 2] = w2 * px + w6 * py + w10 * pz + w14;
            } else {
                const o = t * 12;
                out[i * 3] = combined[o] * px + combined[o + 1] * py + combined[o + 2] * pz + combined[o + 3];
                out[i * 3 + 1] = combined[o + 4] * px + combined[o + 5] * py + combined[o + 6] * pz + combined[o + 7];
                out[i * 3 + 2] = combined[o + 8] * px + combined[o + 9] * py + combined[o + 10] * pz + combined[o + 11];
            }
        }

        this._worldCenters = out;
        this._worldCentersKey = key;
        return out;
    }

    /**
     * Approximate world-space radius of every splat's 3-sigma footprint:
     * 3 * largest axis scale * largest scale factor of its world transform.
     */
    getWorldRadii(): Float32Array {
        const key = `${this.transformVersion}:${this.rowsVersion}`;
        if (this._worldRadii && this._worldRadiiKey === key) return this._worldRadii;

        const n = this.numSplats;
        const out = new Float32Array(n);
        const s0 = this.col('scale_0'), s1 = this.col('scale_1'), s2 = this.col('scale_2');
        const ti = this.transformIndex;
        const combined = this.combinedTransforms();
        const factors = new Float32Array(this.transformPalette.count);
        for (let k = 0; k < factors.length; k++) factors[k] = maxColumnScale(combined, k * 12);

        for (let i = 0; i < n; i++) {
            const m = Math.max(s0[i], s1[i], s2[i]);
            out[i] = 3 * Math.exp(m) * factors[ti[i]];
        }
        this._worldRadii = out;
        this._worldRadiiKey = key;
        return out;
    }

    /**
     * World matrix multiplied by every palette entry, as 3x4 row-major blocks
     * (12 floats per palette index).
     */
    combinedTransforms(): Float32Array {
        const pal = this.transformPalette;
        const out = new Float32Array(pal.count * 12);
        const w = this.worldMatrix;
        const p = pal.data;
        for (let k = 0; k < pal.count; k++) {
            const o = k * TRANSFORM_FLOATS;
            // palette rows: [a b c tx; d e f ty; g h i tz]
            for (let r = 0; r < 3; r++) {
                for (let c = 0; c < 4; c++) {
                    // world row r · palette column c
                    let v = w[r] * p[o + c] + w[4 + r] * p[o + 4 + c] + w[8 + r] * p[o + 8 + c];
                    if (c === 3) v += w[12 + r];
                    out[k * 12 + r * 4 + c] = v;
                }
            }
        }
        return out;
    }

    // ─── Row management (duplicate) ───────────────────────

    /**
     * Append copies of the given rows (with their palette indices) at the end.
     * New rows get `newState`. Returns the index of the first appended row.
     */
    appendRows(rows: Uint32Array, newState: number): number {
        const n = this.numSplats;
        const add = rows.length;
        const total = n + add;
        for (const [name, col] of this.columns) {
            const next = new Float32Array(total);
            next.set(col.subarray(0, n));
            for (let j = 0; j < add; j++) next[n + j] = col[rows[j]];
            this.columns.set(name, next);
        }
        const grow = <T extends Uint8Array | Uint32Array>(arr: T, fill: (j: number) => number): T => {
            const next = new (arr.constructor as { new(len: number): T })(total);
            next.set(arr.subarray(0, n));
            for (let j = 0; j < add; j++) next[n + j] = fill(j);
            return next;
        };
        this.state = grow(this.state, () => newState);
        this.transformIndex = grow(this.transformIndex, (j) => this.transformIndex[rows[j]]);
        this.colorIndex = grow(this.colorIndex, (j) => this.colorIndex[rows[j]]);
        this.numSplats = total;
        this.rowsVersion++;
        this.markStateChanged();
        this.markTransformChanged();
        this.markColorChanged();
        return n;
    }

    /** Drop rows from `start` to the end (undo of appendRows). */
    truncateRows(start: number): void {
        if (start >= this.numSplats) return;
        for (const [name, col] of this.columns) this.columns.set(name, col.slice(0, start));
        this.state = this.state.slice(0, start);
        if (this._markedCount) {
            let count = 0;
            for (let i = 0; i < start; i++) if (this.state[i] & SplatState.marked) count++;
            this._markedCount = count;
        }
        this.transformIndex = this.transformIndex.slice(0, start);
        this.colorIndex = this.colorIndex.slice(0, start);
        this.numSplats = start;
        this.rowsVersion++;
        this.markStateChanged();
        this.markTransformChanged();
        this.markColorChanged();
    }
}

function toFloat32(col: ArrayLike<number>, n: number): Float32Array {
    if (col instanceof Float32Array && col.length === n) return col;
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) out[i] = col[i];
    return out;
}

function maxColumnScale(m: Float32Array, o: number): number {
    // 3x4 row-major: columns are (m[o], m[o+4], m[o+8]) etc.
    const c0 = Math.hypot(m[o], m[o + 4], m[o + 8]);
    const c1 = Math.hypot(m[o + 1], m[o + 5], m[o + 9]);
    const c2 = Math.hypot(m[o + 2], m[o + 6], m[o + 10]);
    return Math.max(c0, c1, c2);
}
